'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { SummaryListingDetails } from '@/src/types/summaryListingDetails.type';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { ListingCard } from '@/src/components/ListingCard';
import { CAROUSEL_CARD_IMAGE_SIZES, ListingCarousel } from '@/src/components/ListingCarousel';
import { useProviderListings } from '@/src/components/features/property/hooks/useProviderListings';
import { BELOW_FOLD_ACTIVATION_ROOT_MARGIN, useViewportActivation } from '@/src/lib/hooks/ui';
import { carouselItem, container, loadingCard, loadingRow } from './providerListingsModule.styles';

type ListingIdentity = {
  aliasEncoded?: string;
  maloneLotIdEncoded?: string;
  uriV2?: string;
  uriMalone?: string;
  uriBuilding?: string;
};

type Props = {
  listing: PropertyDetailListing;
};

const getListingKey = (listing: ListingIdentity, index: number): string =>
  listing.aliasEncoded ??
  listing.maloneLotIdEncoded ??
  listing.uriV2 ??
  listing.uriMalone ??
  listing.uriBuilding ??
  `provider-listing-${index}`;

const getIdentitySet = (listing: ListingIdentity): Set<string> =>
  new Set(
    [listing.aliasEncoded, listing.maloneLotIdEncoded, listing.uriV2, listing.uriMalone, listing.uriBuilding].filter(
      Boolean,
    ) as Array<string>,
  );

const hasMatchingIdentity = (listing: ListingIdentity, currentListingIds: Set<string>): boolean =>
  [listing.aliasEncoded, listing.maloneLotIdEncoded, listing.uriV2, listing.uriMalone, listing.uriBuilding].some(
    (id) => id != null && currentListingIds.has(id),
  );

export const ProviderListingsModule = ({ listing }: Props) => (
  // Client-side HDP-to-HDP navigation keeps this tree mounted, so key the
  // gated content by listing identity to re-arm the viewport gate.
  <ProviderListingsContent key={listing.aliasEncoded ?? listing.maloneLotIdEncoded} listing={listing} />
);

const ProviderListingsContent = ({ listing }: Props) => {
  const t = useTranslations('hdp');
  const aliasEncoded = listing.aliasEncoded ?? null;
  // Below-fold carousel: gate the fetch and card mount on viewport approach.
  const { ref, isActive } = useViewportActivation<HTMLDivElement>({ rootMargin: BELOW_FOLD_ACTIVATION_ROOT_MARGIN });
  const { data, isLoading, error } = useProviderListings(aliasEncoded, { enabled: isActive });

  const providerListings = useMemo(() => {
    const currentListingIds = getIdentitySet(listing);
    const seen = new Set<string>();

    return (data ?? []).filter((providerListing, index) => {
      if (hasMatchingIdentity(providerListing, currentListingIds)) return false;

      const key = getListingKey(providerListing, index);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [data, listing]);

  if (!isActive || isLoading) {
    return (
      <div ref={ref} className={loadingRow} role="status" aria-label={t('loadingProviderListings')} aria-busy="true">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className={loadingCard} />
        ))}
      </div>
    );
  }

  if (error || providerListings.length === 0) {
    return null;
  }

  return (
    <div className={container}>
      <ListingCarousel aria-label={t('providerListingsTitle')}>
        {providerListings.map((providerListing, index) => (
          <div key={getListingKey(providerListing, index)} className={carouselItem}>
            <ListingCard
              clickstreamTriggerLocation={'portfolio_listings_module'}
              listing={providerListing as SummaryListingDetails}
              sizes={CAROUSEL_CARD_IMAGE_SIZES}
            />
          </div>
        ))}
      </ListingCarousel>
    </div>
  );
};
